/**
 * Single owner of the server-initiated pairing-LOSS transition.
 *
 * Ownership map for every credential-adjacent transition (grep for
 * these names rather than re-deriving the map from call sites):
 *   - pairing established   -> `completePairing` (services/pairing.ts)
 *   - user-initiated unpair  -> `disconnect()` (services/disconnect.ts)
 *     — ALWAYS wipes, no guard: explicit user intent, nothing to
 *     protect against.
 *   - server-initiated loss  -> THIS module, `handlePairingLostTransition`
 *     (guarded — see below). Driven by `services/sse.ts`'s SSE
 *     `disconnect` event handler AND its `SSEClient.onPairingLost`
 *     callback (401/403/404 on the connect handshake) — both funnel
 *     here so there is exactly one place that decides whether a
 *     server-initiated signal is safe to act on.
 *   - wipe mechanics         -> `clearLocalState` (services/disconnect.ts)
 *   - keypair identity       -> `ensureKeypair` (services/keys.ts) +
 *     the `clearExcept(["publicKey","secretKey"])` preservation
 *     (adapters/storage.web.ts)
 *
 * THE RACE THIS GUARDS (confirmed in production 2026-05-30):
 * A server-initiated pairing-lost signal can be STALE — an old SSE
 * connection's dying gasp, or a background tab whose connection
 * opened with a token a fresh re-pair has since replaced. Multiple
 * tabs share one IndexedDB, so a stale TAB (not just a stale
 * connection inside the same tab) can trigger this. If the signal
 * fires AFTER a newer pairing already completed, wiping
 * unconditionally destroys the NEW pairing's `token`/`phonePubKey`
 * and boots the user back to PairingPage despite being validly paired
 * a moment ago.
 *
 * THE GUARD: the auth token IS the pairing identity — `completePairing`
 * writes a fresh one on every pair, and nothing else does. The caller
 * captures the token the dying connection was opened with and passes
 * it in as `connectionToken`. We re-read the CURRENT token from
 * storage and compare:
 *   - storage token DIFFERS from `connectionToken` -> a newer pairing
 *     superseded this connection. Skip the wipe entirely; log a
 *     structured skip (never the token values themselves).
 *   - storage token MATCHES, or is absent/undefined (nothing newer to
 *     protect — preserves the pre-existing defensive-wipe behavior for
 *     e.g. a half-finished pairing) -> proceed.
 *
 * THE RESULT CONTRACT: the transition reports which branch ran —
 * `"stale_skipped"` (a guard check tripped; nothing was wiped) or
 * `"wiped"` (`clearLocalState()` completed). `sse.ts` branches its
 * `pairingLostFired` idempotency flag on this: after `"wiped"` the
 * flag stays latched (storage is gone, `paired` flipped false, the
 * App unmounts the stream — nothing left to report). After
 * `"stale_skipped"` the lifecycle SURVIVES — `paired` stays true and
 * the reconnect tick re-opens the stream with the newer token — so
 * the caller must un-latch its flag, or the surviving stream would
 * be permanently deaf: `sseConnected` writes suppressed forever, and
 * a later GENUINE pairing loss on the new connection short-circuited
 * before it ever reaches this module.
 *
 * This is a compare-then-act check against a shared, non-transactional
 * store (no compare-and-swap primitive exists — see
 * `adapters/storage.web.ts`'s `set` comment on read-then-write not
 * being atomic), so it cannot close the race completely. A second,
 * cheaper re-compare immediately before `clearLocalState()` narrows
 * the window further: the truly irreducible gap left over is the
 * time between that second read resolving and `clearLocalState()`
 * actually starting — a single microtask, not the whole async
 * `webStorage.set()` the first check would otherwise straddle. If a
 * re-pair lands in exactly that residual gap, the wipe still
 * proceeds and destroys the new pairing's credentials — but the
 * X25519 keypair survives it (`clearExcept`), so the damage stays
 * bounded to "user has to re-pair", never a permanently
 * undecryptable device.
 */

import { webStorage } from "../adapters/storage.web";
import { logWarn } from "../lib/log";
import { clearLocalState } from "./disconnect";

/**
 * True when `storageToken` reflects a pairing newer than the one
 * `connectionToken` belongs to. An absent/undefined `storageToken`
 * means there is nothing newer to protect (the defensive-wipe case),
 * NOT staleness — so this returns `false` in that case.
 */
function supersedesConnection(
  storageToken: string | undefined,
  connectionToken: string | undefined
): boolean {
  return storageToken !== undefined && storageToken !== connectionToken;
}

/**
 * Run the server-initiated pairing-loss transition: flip the router
 * gate, then wipe local credentials — UNLESS a newer pairing has
 * already superseded the connection that reported the loss.
 *
 * `connectionToken` is the auth token the now-dead SSE connection was
 * opened with (`undefined` if the caller never captured one).
 *
 * Resolves `"wiped"` once `clearLocalState()` has completed, and
 * `"stale_skipped"` when either token check detected a newer pairing
 * (first check: storage fully untouched by this call; second check:
 * only this call's `paired: false` safety-net write has landed — the
 * accepted residual the module docblock describes). Callers MUST
 * branch on the result — see THE RESULT CONTRACT above.
 *
 * Throws when the wipe itself throws — callers that maintain their
 * own re-entry guard (e.g. `sse.ts`'s `pairingLostFired` flag) are
 * expected to reset it in their own catch so a follow-up signal can
 * retry; this function does not swallow any error to preserve that
 * contract.
 */
export async function handlePairingLostTransition(
  connectionToken: string | undefined
): Promise<"wiped" | "stale_skipped"> {
  const { token: currentToken } = await webStorage.get("token");
  if (supersedesConnection(currentToken, connectionToken)) {
    // Never log the token values themselves — only that a skip
    // happened. See the module docblock for why this is safe to
    // treat as a no-op rather than a defensive wipe.
    logWarn("sse", "pairing_lost_stale_ignored");
    return "stale_skipped";
  }

  // Order matters: flip `paired: false` (the App router gate) BEFORE
  // the full wipe below. The two operations aren't atomic — if `set`
  // succeeds and the wipe throws, the router sees `paired: false` and
  // lands the user on PairingPage cleanly; if we did the wipe first
  // and `set` threw, the user would be stuck with `paired: true` but
  // no credentials and no SSE pipeline left to retry. This explicit
  // set is the safety-net. `pairingJustConfirmed` and `sseConnected`
  // ride along — not router-gated, but cheap to flip in the same
  // write. Every other storage key is left alone here; they're all
  // wiped by `clearLocalState` below.
  await webStorage.set({
    paired: false,
    pairingJustConfirmed: false,
    sseConnected: false,
  });

  // Second, cheaper re-compare immediately before the destructive
  // call — narrows the compare-then-act window down to the gap
  // between this read resolving and `clearLocalState()` starting.
  // See the module docblock for why the remaining microsecond-scale
  // gap is an accepted residual risk.
  const { token: recheckToken } = await webStorage.get("token");
  if (supersedesConnection(recheckToken, connectionToken)) {
    logWarn("sse", "pairing_lost_stale_ignored");
    return "stale_skipped";
  }

  // Full local-state wipe — delegates to the same helper the
  // user-initiated disconnect uses (`clearLocalState` in
  // services/disconnect.ts) so server-initiated `pairing_lost` and
  // explicit disconnect leave storage in the SAME clean state. Per-
  // device prefs (`hiddenApps`, `dndSchedule`, `dndSnoozeUntil`,
  // `selfDeviceName`, `peerDevices`, `pairAutoNameResult`,
  // `pairConfirmDismissedAt`) and the stale self `deviceId` all go
  // alongside the credentials. Without this, a re-pair after a lost
  // pairing inherits stale per-device prefs and the next
  // `autoRenameSelfOnFirstPair` PATCHes the wrong (now-deleted)
  // device, 403-ing because the new token has no link to the stale
  // id. `clearLocalState` also drops the browser push subscription
  // and clears the unread badge so the SW doesn't keep firing
  // `unpaired` push outcomes against a now-orphaned subscription.
  await clearLocalState();
  return "wiped";
}
